// Masks an email, keeping only the first and last character of both the username and provider, masking the rest, keeping original length
import Vmask from 'vanilla-masker';
import { DateTime } from 'luxon';
import { Nullable } from './aux-types';

export function maskEmail(email: string): string {
  if (!email) return '';
  const [user, domain] = email.split('@');
  if (!user || !domain) return email;
  const maskedUser = user.replace(/(^.)(.*)(.$)/, (m, a, b, c) => a + '*'.repeat(b.length) + c);
  const [provider, ...rest] = domain.split('.');
  const maskedProvider = provider.replace(/(^.)(.*)(.$)/, (m, a, b, c) => a + '*'.repeat(b.length) + c);
  const maskedDomain = maskedProvider + (rest.length ? `.${rest.join('.')}` : '');
  return `${maskedUser}@${maskedDomain}`;
}

export interface IFormatter {
  maskedValue: string;
  rawValue: string;
}

export const formatMoneyStringToFloat = (value: string, options = { decimalSeparator: ',', thousandSeparator: '.' }) => {
  const result = value.replaceAll(options.thousandSeparator, '').replaceAll(options.decimalSeparator, '.');
  return parseFloat(result);
};

export const formatNumberToBRL = (value: Nullable<number | string>, showZero?: boolean) => {
  if (!value && !showZero) {
    return '-';
  }

  const safeParsedValue = typeof value === 'number' ? value : Number(value || 0);
  return safeParsedValue?.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' });
};

export const formatIntToFloat = (value: number, separator = ',') => {
  return `${(value / 100.0).toFixed(2).replace('.', separator)}`;
};

export const formatDecimalToPercentage = (value: number, separator = ',') => {
  return `${(value * 100.0).toFixed(2).replace('.', separator)}%`;
};

export const removeDoubleSpacesAndTrim = (value: string) => {
  return value.trim().replace(/\s+/g, ' ');
};

export const chopKeyAccessNumber = (value: string) => {
  return `${value.trim().substring(0, 5)}...${value.trim().substring(value.length - 5, value.length)}`;
};

const formatter = (mask: string, value?: string): IFormatter => {
  const maskedValue = Vmask.toPattern(value || '', mask);
  return { maskedValue, rawValue: maskedValue.replace(/\D/g, '') };
};

export const phoneFormatter = (value?: string) => formatter('(99) 9 9999-9999', value);
export const cnpjFormatter = (value?: string) => formatter('99.999.999/9999-99', value);
export const cpfFormatter = (value?: string) => formatter('999.999.999-99', value);

export const generateRootFullCnpj = (value?: string | number) => {
  const root = String(value || '').replace(/\D/g, '');

  if (!root) return '-';
  if (!/^\d{8}$/.test(root)) return value ? String(value) : '-';

  const base = `${root}0001`;

  const calculateDigit = (numbers: string, weights: number[]) => {
    const sum = numbers.split('').reduce((acc, digit, index) => acc + Number(digit) * weights[index], 0);
    const remainder = sum % 11;

    return remainder < 2 ? 0 : 11 - remainder;
  };

  const firstDigit = calculateDigit(base, [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  const secondDigit = calculateDigit(`${base}${firstDigit}`, [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);

  return cnpjFormatter(`${base}${firstDigit}${secondDigit}`).maskedValue;
};

export const sliceStringBiggerThan = (text: string | undefined, size: number) => {
  if (!text) {
    return '';
  }

  return text.length > size ? `${text.slice(0, size - 3)}...` : text;
};

export const nullifyObjectEmptyStrings = (obj: object) => {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    return {
      ...acc,
      [key]: typeof value === 'string' && value === '' ? null : value,
    };
  }, {});
};

export const numberOrEmptyString = (value: Nullable<string | number>) => {
  const isNullable = value === null || value === undefined;

  if (isNullable) return '-';

  const parsedValue = Number(value);

  return Number.isNaN(parsedValue) ? '-' : parsedValue;
};

export const formatTaxNumber = (value?: string) => {
  const raw = (value || '').replace(/\D/g, '');
  if (!raw) return '-';
  if (raw.length === 11) return cpfFormatter(raw).maskedValue;
  if (raw.length === 14) return cnpjFormatter(raw).maskedValue;
  return value || '-';
};

/** Taker/remetente on arrecadação: CPF as "CPF 000.000.000-00", else CNPJ from 8-digit root. */
export const formatTakerDocument = (value?: string) => {
  const digits = (value || '').replace(/\D/g, '');
  if (digits.length === 11) {
    return `${cpfFormatter(digits).maskedValue}`;
  }
  return generateRootFullCnpj(value);
};

const BRAZIL_TIME_ZONE = 'America/Sao_Paulo';

export const formatDateTime = (value?: string) => {
  if (!value) return '-';

  // Timestamps are stored in UTC and may arrive without an offset.
  const dateTime = DateTime.fromISO(value, { zone: 'utc' }).setZone(BRAZIL_TIME_ZONE);
  if (!dateTime.isValid) return value;

  return dateTime.toFormat("dd/MM/yyyy 'às' HH:mm");
};

export const toOptionalTrimmedString = (value?: string): string | undefined => {
  const trimmedValue = value?.trim();
  return trimmedValue || undefined;
};
